/**
 * PokerStars Hand History serializer + parser.
 *
 * Spec: FIRST_INSTRUCTION.md §4.8 L302–L313 (PokerStars HH is the v1 standard;
 * round-trip serialize ↔ parse is the correctness oracle).
 *
 * Design (M4-storage.md G1, G2, G3):
 *  - The serializer is a fold over a HandRecord — the canonical lossless
 *    representation of a played hand. Engine events are converted to a
 *    HandRecord by `recordFromEngine` so the writer never sees mutable state.
 *  - Output dialect stays close to PokerStars so PokerTracker / Hand2Note can
 *    import it. Two non-standard niceties:
 *      1. A `# meta:` comment line carries seed + playedAt so they survive
 *         round-trip without polluting standard fields.
 *      2. SUMMARY uses `mucked [c c]` to record folded players' hole cards
 *         (PokerStars only emits this at showdown). Real PS parsers tolerate
 *         the extra lines, and ours reads them straight back into the map.
 */
import type {
  Action,
  BlindLevel,
  Card,
  Event,
  HandState,
  PlayerId,
  Seat,
  Street,
} from '@/engine/types';
import type { HandPayout } from './db';

export type SeatEntry = {
  seat: Seat;
  playerId: PlayerId;
  /** Stack at the start of the hand, before posting blinds/antes. */
  startStack: number;
};

/**
 * Canonical lossless representation of a played hand. Both serializer
 * and parser pivot through this type so round-trip is identity.
 */
export type HandRecord = {
  tournamentId: string;
  handNumber: number;
  level: BlindLevel;
  seed: string;
  /** Wall-clock at hand end (ms since epoch). */
  playedAt: number;
  button: Seat;
  seats: ReadonlyArray<SeatEntry>;
  /** The seat the human plays — only their cards get the standard "Dealt to" line. */
  heroId: PlayerId;
  /** Every player whose hole cards were observed (we observe all, by design). */
  holeCardsByPlayer: Readonly<Record<PlayerId, readonly [Card, Card]>>;
  actions: ReadonlyArray<Action>;
  board: ReadonlyArray<Card>;
  payouts: ReadonlyArray<HandPayout>;
  /** Sum of all pots (excludes uncalled chips that were returned). */
  totalPot: number;
};

export type EngineHandInput = {
  tournamentId: string;
  initialState: HandState;
  finalState: HandState;
  events: ReadonlyArray<Event>;
  startOfHandStacks: Readonly<Record<PlayerId, number>>;
  heroId: PlayerId;
  playedAt: number;
};

const STREET_HEADER: Partial<Record<Street, string>> = {
  flop: '*** FLOP ***',
  turn: '*** TURN ***',
  river: '*** RIVER ***',
};

const SUMMARY_STREET_NAMES: Record<Street, string> = {
  preflop: 'before Flop',
  flop: 'on the Flop',
  turn: 'on the Turn',
  river: 'on the River',
  showdown: 'at Showdown',
  payout: 'at Showdown',
};

function pad2(n: number): string {
  return n < 10 ? `0${n}` : `${n}`;
}

/**
 * PokerStars timestamps look like `2024/01/02 15:30:45 ET`. We render in UTC
 * but tag with `ET` because PokerStars/PT only ever expect that suffix —
 * actual offset doesn't matter for parsing, and we need byte stability
 * across machines (so no local timezone).
 */
export function formatPsTimestamp(ms: number): string {
  const d = new Date(ms);
  return [
    `${d.getUTCFullYear()}/${pad2(d.getUTCMonth() + 1)}/${pad2(d.getUTCDate())}`,
    `${pad2(d.getUTCHours())}:${pad2(d.getUTCMinutes())}:${pad2(d.getUTCSeconds())}`,
    'ET',
  ].join(' ');
}

export function parsePsTimestamp(text: string): number {
  const m = text.match(/^(\d{4})\/(\d{2})\/(\d{2}) (\d{2}):(\d{2}):(\d{2}) ET$/);
  if (!m) throw new HhParseError(`Bad timestamp: ${text}`);
  const [, y, mo, da, h, mi, s] = m;
  return Date.UTC(Number(y), Number(mo) - 1, Number(da), Number(h), Number(mi), Number(s));
}

function fmtCards(cards: ReadonlyArray<Card>): string {
  return `[${cards.join(' ')}]`;
}

function parseCardArray(text: string): Card[] {
  const m = text.match(/^\[(.*)\]$/);
  if (!m) throw new HhParseError(`Bad card array: ${text}`);
  const inner = m[1] ?? '';
  if (inner.trim() === '') return [];
  return inner.trim().split(/\s+/);
}

function holeCardPair(text: string): readonly [Card, Card] {
  const arr = parseCardArray(text);
  if (arr.length !== 2) throw new HhParseError(`Hole cards must be 2 cards: ${text}`);
  return [arr[0] as Card, arr[1] as Card];
}

/**
 * Compute heads-up flag from seats only. Mirrors engine's headsUp logic.
 */
function isHeadsUpRecord(seats: ReadonlyArray<SeatEntry>): boolean {
  return seats.length === 2;
}

function sortedSeats(seats: ReadonlyArray<SeatEntry>): Seat[] {
  return [...seats].map((s) => s.seat).sort((a, b) => a - b);
}

function nextSeatClockwise(from: Seat, available: ReadonlyArray<Seat>): Seat {
  if (available.length === 0) {
    throw new Error('nextSeatClockwise: no available seats');
  }
  const sorted = [...available].sort((a, b) => a - b);
  for (const s of sorted) {
    if (s > from) return s;
  }
  return sorted[0] as Seat;
}

/**
 * Determine sb / bb seats per the engine's startHand logic.
 * Pure function of (button, seats).
 */
export function blindSeats(
  button: Seat,
  seats: ReadonlyArray<SeatEntry>,
): { sbSeat: Seat; bbSeat: Seat } {
  const all = sortedSeats(seats);
  const headsUp = isHeadsUpRecord(seats);
  const sbSeat: Seat = headsUp ? button : nextSeatClockwise(button, all);
  const bbSeat: Seat = nextSeatClockwise(sbSeat, all);
  return { sbSeat, bbSeat };
}

function idBySeat(seats: ReadonlyArray<SeatEntry>, seat: Seat): PlayerId | undefined {
  return seats.find((s) => s.seat === seat)?.playerId;
}

// =====================================================================
// SERIALIZE
// =====================================================================

type StreetCtx = {
  street: Street;
  currentBet: number;
  /** Per-player streetContribution (resets each street). */
  streetContrib: Map<PlayerId, number>;
  /** Per-player handContribution (running total). */
  handContrib: Map<PlayerId, number>;
  folded: Set<PlayerId>;
  allIn: Set<PlayerId>;
};

function freshStreet(street: Street): StreetCtx {
  return {
    street,
    currentBet: 0,
    streetContrib: new Map(),
    handContrib: new Map(),
    folded: new Set(),
    allIn: new Set(),
  };
}

function actionLine(action: Action, ctx: StreetCtx): string {
  const id = action.playerId;
  switch (action.type) {
    case 'fold':
      return `${id}: folds`;
    case 'check':
      return `${id}: checks`;
    case 'call': {
      const allInSuffix = ctx.allIn.has(id) ? ' and is all-in' : '';
      return `${id}: calls ${action.amount}${allInSuffix}`;
    }
    case 'bet': {
      const allInSuffix = ctx.allIn.has(id) ? ' and is all-in' : '';
      return `${id}: bets ${action.amount}${allInSuffix}`;
    }
    case 'raise': {
      const prior = ctx.streetContrib.get(id) ?? 0;
      const before = ctx.currentBet;
      const raiseBy = action.to - before;
      const allInSuffix = ctx.allIn.has(id) ? ' and is all-in' : '';
      // Subtract prior so "raises X to Y" reports the increment over previous
      // currentBet (PokerStars convention), not the player's own delta.
      void prior;
      return `${id}: raises ${raiseBy} to ${action.to}${allInSuffix}`;
    }
    case 'allin': {
      // Engine semantics (rules.ts L100, betting.ts L88): action.amount is the
      // player's NEW streetContribution after this action (streetContrib + stack),
      // not the incremental chips committed. Convert to incremental for HH lines.
      const newTotal = action.amount;
      const prior = ctx.streetContrib.get(id) ?? 0;
      const incremental = newTotal - prior;
      const before = ctx.currentBet;
      if (newTotal > before) {
        if (before === 0) {
          return `${id}: bets ${incremental} and is all-in`;
        }
        const raiseBy = newTotal - before;
        return `${id}: raises ${raiseBy} to ${newTotal} and is all-in`;
      }
      return `${id}: calls ${incremental} and is all-in`;
    }
  }
}

/** Mutate ctx by applying `action`. Mirrors engine's applyAction at HH granularity. */
function advanceCtx(
  ctx: StreetCtx,
  action: Action,
  startStacks: ReadonlyMap<PlayerId, number>,
): void {
  const id = action.playerId;
  const prior = ctx.streetContrib.get(id) ?? 0;
  const handPrior = ctx.handContrib.get(id) ?? 0;
  switch (action.type) {
    case 'fold':
      ctx.folded.add(id);
      break;
    case 'check':
      break;
    case 'call':
      ctx.streetContrib.set(id, prior + action.amount);
      ctx.handContrib.set(id, handPrior + action.amount);
      break;
    case 'bet':
      ctx.streetContrib.set(id, prior + action.amount);
      ctx.handContrib.set(id, handPrior + action.amount);
      ctx.currentBet = prior + action.amount;
      break;
    case 'raise': {
      const delta = action.to - prior;
      ctx.streetContrib.set(id, action.to);
      ctx.handContrib.set(id, handPrior + delta);
      ctx.currentBet = action.to;
      break;
    }
    case 'allin': {
      // action.amount is the new streetContribution (engine semantics).
      const newTotal = action.amount;
      const incremental = newTotal - prior;
      ctx.streetContrib.set(id, newTotal);
      ctx.handContrib.set(id, handPrior + incremental);
      if (newTotal > ctx.currentBet) ctx.currentBet = newTotal;
      ctx.allIn.add(id);
      break;
    }
  }
  // Detect implicit all-in: a `call`/`bet`/`raise` action that drains the
  // player's stack must mark them all-in. Without this, the partitioner
  // keeps "live" players whose streetContrib trails currentBet forever and
  // never closes the betting round, dumping subsequent street actions into
  // the wrong bucket.
  const startStack = startStacks.get(id);
  if (startStack !== undefined && (ctx.handContrib.get(id) ?? 0) >= startStack) {
    ctx.allIn.add(id);
  }
}

/**
 * Compute the "uncalled bet returned to" portion when the hand ends without
 * showdown. Mirrors engine.returnUncalledBets: uncalled = topNonFolded -
 * second-highest contribution.
 */
function computeUncalled(
  finalContrib: ReadonlyMap<PlayerId, number>,
  folded: ReadonlySet<PlayerId>,
): { winner: PlayerId; amount: number } | null {
  const entries = Array.from(finalContrib.entries());
  const nonFolded = entries.filter(([id]) => !folded.has(id));
  if (nonFolded.length === 0) return null;
  const sorted = [...nonFolded].sort((a, b) => b[1] - a[1]);
  const top = sorted[0] as [PlayerId, number];
  const others = entries.filter(([id]) => id !== top[0]);
  const maxOther = others.reduce((m, [, v]) => (v > m ? v : m), 0);
  const uncalled = top[1] - maxOther;
  if (uncalled <= 0) return null;
  return { winner: top[0], amount: uncalled };
}

export function serializeHand(record: HandRecord): string {
  const out: string[] = [];
  const { tournamentId, handNumber, level, seed, playedAt, button, seats, heroId } = record;

  // --- HEADER ----------------------------------------------------------
  // PokerStars timestamps are second-resolution. Floor playedAt to seconds
  // so the meta line and header agree exactly on round-trip — the parser
  // requires both values to match.
  const playedAtSec = Math.floor(playedAt / 1000) * 1000;
  out.push(`# meta: seed=${seed} playedAt=${playedAtSec}`);
  out.push(
    `PokerStars Hand #${tournamentId}-${handNumber}: Tournament #${tournamentId}, ` +
      `0+0 USD Hold'em No Limit - Level ${level.level} (${level.sb}/${level.bb}) - ${formatPsTimestamp(playedAtSec)}`,
  );
  out.push(`Table '${tournamentId} 1' 6-max Seat #${button + 1} is the button`);
  for (const s of [...seats].sort((a, b) => a.seat - b.seat)) {
    out.push(`Seat ${s.seat + 1}: ${s.playerId} (${s.startStack} in chips)`);
  }

  // --- BLIND + ANTE POSTING -------------------------------------------
  const startStacks = new Map<PlayerId, number>(seats.map((s) => [s.playerId, s.startStack]));
  const ctx = freshStreet('preflop');
  const { sbSeat, bbSeat } = blindSeats(button, seats);
  const sbId = idBySeat(seats, sbSeat);
  const bbId = idBySeat(seats, bbSeat);
  if (!sbId || !bbId) {
    throw new Error(`serializeHand: blind seats missing in record (sb=${sbSeat} bb=${bbSeat})`);
  }

  // Ante goes to BB only (engine §startHand). It posts BEFORE the blind line.
  if (level.ante > 0) {
    const bbStart = startStacks.get(bbId) ?? 0;
    const antePosted = Math.min(level.ante, bbStart);
    const remaining = bbStart - antePosted;
    ctx.handContrib.set(bbId, antePosted);
    if (remaining === 0 && antePosted > 0) ctx.allIn.add(bbId);
    if (antePosted > 0) {
      const allInSuffix = ctx.allIn.has(bbId) && remaining === 0 ? ' and is all-in' : '';
      out.push(`${bbId}: posts the ante ${antePosted}${allInSuffix}`);
    }
  }

  // SB
  const sbStart = startStacks.get(sbId) ?? 0;
  const sbPosted = Math.min(level.sb, sbStart);
  if (sbPosted > 0) {
    ctx.streetContrib.set(sbId, sbPosted);
    ctx.handContrib.set(sbId, (ctx.handContrib.get(sbId) ?? 0) + sbPosted);
    if (sbStart - sbPosted === 0) ctx.allIn.add(sbId);
    const allInSuffix = ctx.allIn.has(sbId) ? ' and is all-in' : '';
    out.push(`${sbId}: posts small blind ${sbPosted}${allInSuffix}`);
  }

  // BB
  const bbStart = startStacks.get(bbId) ?? 0;
  const anteAlready = level.ante > 0 ? Math.min(level.ante, bbStart) : 0;
  const bbAvailable = bbStart - anteAlready;
  const bbPosted = Math.min(level.bb, bbAvailable);
  if (bbPosted > 0) {
    ctx.streetContrib.set(bbId, (ctx.streetContrib.get(bbId) ?? 0) + bbPosted);
    ctx.handContrib.set(bbId, (ctx.handContrib.get(bbId) ?? 0) + bbPosted);
    if (bbAvailable - bbPosted === 0) ctx.allIn.add(bbId);
    if (bbPosted > ctx.currentBet) ctx.currentBet = bbPosted;
    const allInSuffix = ctx.allIn.has(bbId) ? ' and is all-in' : '';
    out.push(`${bbId}: posts big blind ${bbPosted}${allInSuffix}`);
  }

  // --- HOLE CARDS ------------------------------------------------------
  out.push('*** HOLE CARDS ***');
  const heroCards = record.holeCardsByPlayer[heroId];
  if (heroCards) {
    out.push(`Dealt to ${heroId} ${fmtCards(heroCards)}`);
  }

  // --- ACTIONS + STREETS ----------------------------------------------
  const boardByStreet: Record<Street, ReadonlyArray<Card>> = {
    preflop: [],
    flop: record.board.slice(0, 3),
    turn: record.board.slice(0, 4),
    river: record.board.slice(0, 5),
    showdown: record.board,
    payout: record.board,
  };

  const actionsByStreet: Record<Street, Action[]> = {
    preflop: [],
    flop: [],
    turn: [],
    river: [],
    showdown: [],
    payout: [],
  };
  // Re-walk actions, partitioning by street. We don't have street markers in
  // the action list; reconstruct via running ctx that mirrors the engine's
  // street-completion check.
  partitionActionsByStreet(record, actionsByStreet, sbId, bbId);

  const streetsInOrder: Street[] = ['preflop', 'flop', 'turn', 'river'];
  // Reset ctx — partitioning had its own; we need a fresh one to drive lines.
  resetCtxForPreflop(ctx, sbId, bbId, level, startStacks);

  for (const street of streetsInOrder) {
    if (street !== 'preflop') {
      const board = boardByStreet[street];
      if (board.length === 0) continue;
      const header = STREET_HEADER[street];
      if (!header) continue;
      out.push(`${header} ${fmtCards(board)}`);
      // Reset street-scoped fields (engine resets streetContribution and
      // currentBet/lastRaise on streetAdvance).
      ctx.street = street;
      ctx.streetContrib = new Map();
      ctx.currentBet = 0;
    }
    for (const action of actionsByStreet[street]) {
      // Pre-compute allIn classification BEFORE advancing ctx so the line
      // text reads from the right state.
      if (action.type === 'allin') {
        ctx.allIn.add(action.playerId);
      } else if (action.type === 'call' || action.type === 'bet' || action.type === 'raise') {
        const prior = ctx.streetContrib.get(action.playerId) ?? 0;
        const stackAtStart = startStacks.get(action.playerId) ?? 0;
        const handPrior = ctx.handContrib.get(action.playerId) ?? 0;
        let delta = 0;
        if (action.type === 'call') delta = action.amount;
        else if (action.type === 'bet') delta = action.amount;
        else delta = action.to - prior;
        if (handPrior + delta >= stackAtStart) {
          ctx.allIn.add(action.playerId);
        }
      }
      out.push(actionLine(action, ctx));
      advanceCtx(ctx, action, startStacks);
    }
  }

  // --- SHOWDOWN / SUMMARY ---------------------------------------------
  const nonFolded = seats.filter((s) => !ctx.folded.has(s.playerId));
  const showdownReached = nonFolded.length > 1 && record.board.length === 5;

  // Uncalled bet returned (only when hand ended pre-showdown via folds).
  const uncalled = computeUncalled(ctx.handContrib, ctx.folded);
  if (!showdownReached && uncalled) {
    out.push(`Uncalled bet (${uncalled.amount}) returned to ${uncalled.winner}`);
    // Roll back the winner's contribution so SUMMARY pot total matches reality.
    ctx.handContrib.set(
      uncalled.winner,
      (ctx.handContrib.get(uncalled.winner) ?? 0) - uncalled.amount,
    );
  }

  // Pot collection lines.
  const totalCollectedByPlayer = new Map<PlayerId, number>();
  for (const p of record.payouts) {
    totalCollectedByPlayer.set(
      p.playerId,
      (totalCollectedByPlayer.get(p.playerId) ?? 0) + p.amount,
    );
  }

  if (showdownReached) {
    out.push('*** SHOW DOWN ***');
    for (const s of nonFolded) {
      const cards = record.holeCardsByPlayer[s.playerId];
      if (!cards) continue;
      const wonAmount = totalCollectedByPlayer.get(s.playerId) ?? 0;
      const payoutEntry = record.payouts.find((p) => p.playerId === s.playerId);
      const desc = payoutEntry?.handDescription;
      if (wonAmount > 0) {
        out.push(
          `${s.playerId}: shows ${fmtCards(cards)} and won ${wonAmount}` +
            (desc ? ` with ${desc}` : ''),
        );
      } else {
        out.push(`${s.playerId}: shows ${fmtCards(cards)} and lost${desc ? ` with ${desc}` : ''}`);
      }
    }
  } else {
    // Single-winner uncontested. Emit the standard "X collected Y" line.
    for (const [id, amount] of totalCollectedByPlayer.entries()) {
      out.push(`${id} collected ${amount} from pot`);
    }
  }

  // --- SUMMARY ---------------------------------------------------------
  out.push('*** SUMMARY ***');
  out.push(`Total pot ${record.totalPot} | Rake 0`);
  if (record.board.length > 0) {
    out.push(`Board ${fmtCards(record.board)}`);
  }
  for (const s of [...seats].sort((a, b) => a.seat - b.seat)) {
    const role: string[] = [];
    if (s.seat === button) role.push('button');
    if (s.playerId === sbId) role.push('small blind');
    if (s.playerId === bbId) role.push('big blind');
    const roleStr = role.length > 0 ? ` (${role.join(') (')})` : '';
    const collected = totalCollectedByPlayer.get(s.playerId) ?? 0;
    const folded = ctx.folded.has(s.playerId);
    const cards = record.holeCardsByPlayer[s.playerId];
    let outcome: string;
    if (folded) {
      outcome = `folded ${SUMMARY_STREET_NAMES[detectFoldStreet(record, s.playerId, sbId, bbId, level)]}`;
      if (cards) {
        out.push(`Seat ${s.seat + 1}: ${s.playerId}${roleStr} ${outcome}`);
        out.push(`Seat ${s.seat + 1}: ${s.playerId} mucked ${fmtCards(cards)}`);
        continue;
      }
    } else if (collected > 0) {
      const desc = record.payouts.find((p) => p.playerId === s.playerId)?.handDescription;
      const showed = cards ? ` showed ${fmtCards(cards)} and` : '';
      const descStr = desc ? ` with ${desc}` : '';
      outcome = `${showed.trim()} won (${collected})${descStr}`.trim();
    } else if (cards) {
      const desc = record.payouts.find((p) => p.playerId === s.playerId)?.handDescription;
      outcome = `showed ${fmtCards(cards)} and lost${desc ? ` with ${desc}` : ''}`;
    } else {
      outcome = `mucked at showdown`;
    }
    out.push(`Seat ${s.seat + 1}: ${s.playerId}${roleStr} ${outcome}`);
  }

  return `${out.join('\n')}\n`;
}

/** Detect on which street a player folded by replaying actions. */
function detectFoldStreet(
  record: HandRecord,
  target: PlayerId,
  sbId: PlayerId,
  bbId: PlayerId,
  level: BlindLevel,
): Street {
  const startStacks = new Map<PlayerId, number>(
    record.seats.map((s) => [s.playerId, s.startStack]),
  );
  const ctx = freshStreet('preflop');
  resetCtxForPreflop(ctx, sbId, bbId, level, startStacks);
  const partitioned: Record<Street, Action[]> = {
    preflop: [],
    flop: [],
    turn: [],
    river: [],
    showdown: [],
    payout: [],
  };
  partitionActionsByStreet(record, partitioned, sbId, bbId);
  for (const street of ['preflop', 'flop', 'turn', 'river'] as const) {
    for (const action of partitioned[street]) {
      if (action.playerId === target && action.type === 'fold') return street;
      advanceCtx(ctx, action, startStacks);
    }
  }
  return 'preflop';
}

/** Restore preflop state with blinds + ante posted. */
function resetCtxForPreflop(
  ctx: StreetCtx,
  sbId: PlayerId,
  bbId: PlayerId,
  level: BlindLevel,
  startStacks: ReadonlyMap<PlayerId, number>,
): void {
  ctx.street = 'preflop';
  ctx.currentBet = 0;
  ctx.streetContrib.clear();
  ctx.handContrib.clear();
  ctx.folded.clear();
  ctx.allIn.clear();

  const bbStart = startStacks.get(bbId) ?? 0;
  if (level.ante > 0) {
    const ante = Math.min(level.ante, bbStart);
    ctx.handContrib.set(bbId, ante);
    if (ante === bbStart) ctx.allIn.add(bbId);
  }
  const sbStart = startStacks.get(sbId) ?? 0;
  const sbPosted = Math.min(level.sb, sbStart);
  if (sbPosted > 0) {
    ctx.streetContrib.set(sbId, sbPosted);
    ctx.handContrib.set(sbId, (ctx.handContrib.get(sbId) ?? 0) + sbPosted);
    if (sbStart === sbPosted) ctx.allIn.add(sbId);
  }
  const anteAlready = level.ante > 0 ? Math.min(level.ante, bbStart) : 0;
  const bbAvailable = bbStart - anteAlready;
  const bbPosted = Math.min(level.bb, bbAvailable);
  if (bbPosted > 0) {
    ctx.streetContrib.set(bbId, (ctx.streetContrib.get(bbId) ?? 0) + bbPosted);
    ctx.handContrib.set(bbId, (ctx.handContrib.get(bbId) ?? 0) + bbPosted);
    if (bbPosted === bbAvailable) ctx.allIn.add(bbId);
    if (bbPosted > ctx.currentBet) ctx.currentBet = bbPosted;
  }
}

/**
 * Partition the flat action list into street buckets by walking forward and
 * detecting when the betting round closes. Mirrors reducer.isStreetComplete:
 * a round closes when all non-folded acting players have matched currentBet
 * AND have acted since the last aggression. Players who go all-in are no
 * longer "acting".
 */
function partitionActionsByStreet(
  record: HandRecord,
  out: Record<Street, Action[]>,
  sbId: PlayerId,
  bbId: PlayerId,
): void {
  const startStacks = new Map<PlayerId, number>(
    record.seats.map((s) => [s.playerId, s.startStack]),
  );
  const ctx = freshStreet('preflop');
  resetCtxForPreflop(ctx, sbId, bbId, record.level, startStacks);

  // Track who has acted since last aggression (per street).
  let actedInRound = new Set<PlayerId>();
  let street: Street = 'preflop';
  // Helper: non-folded, non-all-in players that still need to act this street.
  const livePlayers = (): PlayerId[] =>
    record.seats.map((s) => s.playerId).filter((id) => !ctx.folded.has(id) && !ctx.allIn.has(id));

  const streetSequence: Street[] = ['flop', 'turn', 'river'];

  for (const action of record.actions) {
    out[street].push(action);
    const before = ctx.currentBet;
    advanceCtx(ctx, action, startStacks);
    if (
      action.type === 'bet' ||
      action.type === 'raise' ||
      (action.type === 'allin' && ctx.currentBet > before)
    ) {
      actedInRound = new Set([action.playerId]);
    } else if (action.type !== 'fold' || action.type === 'fold') {
      actedInRound.add(action.playerId);
    }
    // Check if the street completes after this action.
    const live = livePlayers();
    const allMatched = live.every((id) => (ctx.streetContrib.get(id) ?? 0) === ctx.currentBet);
    const allActed = live.every((id) => actedInRound.has(id));
    const handOver = record.seats.filter((s) => !ctx.folded.has(s.playerId)).length <= 1;
    if (handOver) break;
    if (live.length === 0 || (allMatched && allActed)) {
      // Advance street.
      let next: Street | undefined;
      if (street === 'preflop') {
        next = streetSequence[0];
      } else {
        const nextIdx = streetSequence.indexOf(street as 'flop' | 'turn' | 'river');
        next = streetSequence[nextIdx + 1];
      }
      if (!next) break;
      street = next;
      ctx.street = street;
      ctx.currentBet = 0;
      ctx.streetContrib = new Map();
      actedInRound = new Set();
    }
  }
}

// =====================================================================
// recordFromEngine
// =====================================================================

/** Bridge: convert engine output (events + finalState) to a HandRecord. */
export function recordFromEngine(input: EngineHandInput): HandRecord {
  const { tournamentId, initialState, finalState, events, startOfHandStacks, playedAt } = input;
  const seats: SeatEntry[] = initialState.players
    .filter(
      (p) => (startOfHandStacks[p.id] ?? 0) > 0 || (p.holeCards !== undefined && !p.eliminated),
    )
    .map((p) => ({
      seat: p.seat,
      playerId: p.id,
      startStack: startOfHandStacks[p.id] ?? p.stack,
    }));
  // Fallback: if the declared hero isn't dealt into this hand (e.g. eliminated
  // a prior hand), pick the lowest-seat dealt-in player so HH always carries
  // a "Dealt to …" line. Round-trip depends on the line being present.
  const heroId: PlayerId = seats.some((s) => s.playerId === input.heroId)
    ? input.heroId
    : (seats[0]?.playerId ?? input.heroId);

  const holeCardsByPlayer: Record<PlayerId, readonly [Card, Card]> = {};
  for (const ev of events) {
    if (ev.type === 'deal') {
      holeCardsByPlayer[ev.playerId] = ev.cards;
    }
  }
  // Fall back to finalState.holeCards if events were filtered.
  for (const p of finalState.players) {
    if (p.holeCards && !holeCardsByPlayer[p.id]) {
      holeCardsByPlayer[p.id] = p.holeCards;
    }
  }

  const actions: Action[] = events
    .filter((e): e is Extract<Event, { type: 'actionTaken' }> => e.type === 'actionTaken')
    .map((e) => e.action);

  const totalPot = finalState.results
    ? finalState.results.payouts.reduce((sum, p) => sum + p.amount, 0)
    : 0;

  return {
    tournamentId,
    handNumber: initialState.handNumber,
    level: initialState.level,
    seed: initialState.seed,
    playedAt,
    button: initialState.button,
    seats,
    heroId,
    holeCardsByPlayer,
    actions,
    board: finalState.board,
    payouts: finalState.results?.payouts ?? [],
    totalPot,
  };
}

// =====================================================================
// PARSE (Step 3)
// =====================================================================

export class HhParseError extends Error {
  constructor(
    message: string,
    public readonly line?: number,
  ) {
    super(message);
    this.name = 'HhParseError';
  }
}

const META_RE = /^# meta: seed=(\S+) playedAt=(\d+)$/;
const HEADER_RE =
  /^PokerStars Hand #([\w-]+)-(\d+): Tournament #([\w-]+), .* Level (\d+) \((\d+)\/(\d+)\) - (\d{4}\/\d{2}\/\d{2} \d{2}:\d{2}:\d{2} ET)$/;
const TABLE_RE = /^Table '([\w-]+) (\d+)' (\d+)-max Seat #(\d+) is the button$/;
const SEAT_RE = /^Seat (\d+): (\S+) \((\d+) in chips\)$/;
const ANTE_RE = /^(\S+): posts the ante (\d+)( and is all-in)?$/;
const SB_RE = /^(\S+): posts small blind (\d+)( and is all-in)?$/;
const BB_RE = /^(\S+): posts big blind (\d+)( and is all-in)?$/;
const DEALT_RE = /^Dealt to (\S+) (\[\w{2} \w{2}\])$/;
const STREET_RE = /^\*\*\* (FLOP|TURN|RIVER) \*\*\* (\[[^\]]+\])(?: (\[[^\]]+\]))?$/;
const ACTION_FOLD = /^(\S+): folds$/;
const ACTION_CHECK = /^(\S+): checks$/;
const ACTION_CALL = /^(\S+): calls (\d+)( and is all-in)?$/;
const ACTION_BET = /^(\S+): bets (\d+)( and is all-in)?$/;
const ACTION_RAISE = /^(\S+): raises (\d+) to (\d+)( and is all-in)?$/;
const UNCALLED_RE = /^Uncalled bet \((\d+)\) returned to (\S+)$/;
const SHOWS_RE = /^(\S+): shows (\[[^\]]+\]) and (won|lost)(?: (\d+))?(?: with (.+))?$/;
const COLLECTED_RE = /^(\S+) collected (\d+) from pot$/;
const TOTAL_POT_RE = /^Total pot (\d+) \| Rake 0$/;
const BOARD_RE = /^Board (\[[^\]]+\])$/;
const SUMMARY_SEAT_RE = /^Seat \d+: (\S+)(?: \(([^)]+(?:\) \([^)]+)*)\))? (.+)$/;
const SUMMARY_MUCKED_RE = /^Seat \d+: (\S+) mucked (\[[^\]]+\])$/;
const SUMMARY_SHOWED_RE = /^Seat \d+: (\S+)[^[]*showed (\[[^\]]+\])/;

/**
 * Convert a serialized HH text back into the canonical HandRecord.
 *
 * Strategy: single forward pass; an internal StreetCtx mirrors the engine's
 * betting state so that "raises Y to Z and is all-in" can be converted to an
 * `allin` Action with the correct `amount` (chips committed by this action,
 * not the new street total).
 */
export function parseHand(text: string): HandRecord {
  const lines = text
    .replace(/\r\n/g, '\n')
    .split('\n')
    .filter((l) => l.length > 0);
  let li = 0;
  const fail = (msg: string): never => {
    throw new HhParseError(`${msg} (line ${li + 1}: ${JSON.stringify(lines[li])})`, li + 1);
  };
  const matchOrFail = (re: RegExp, msg: string): RegExpMatchArray => {
    const line = lines[li];
    const m = line?.match(re);
    if (!m) fail(msg);
    return m as RegExpMatchArray;
  };
  const group = (m: RegExpMatchArray, i: number): string => {
    const g = m[i];
    if (g === undefined) fail(`Missing capture group ${i}`);
    return g as string;
  };

  // --- META ----------------------------------------------------------
  const metaMatch = matchOrFail(META_RE, 'Expected meta header (# meta: seed=… playedAt=…)');
  const seed = group(metaMatch, 1);
  const playedAtMeta = Number(group(metaMatch, 2));
  li += 1;

  // --- HEADER --------------------------------------------------------
  const headerMatch = matchOrFail(HEADER_RE, 'Expected PokerStars Hand header');
  const tournamentId = group(headerMatch, 3);
  const handNumber = Number(group(headerMatch, 2));
  const levelNum = Number(group(headerMatch, 4));
  const sb = Number(group(headerMatch, 5));
  const bb = Number(group(headerMatch, 6));
  const playedAtHeader = parsePsTimestamp(group(headerMatch, 7));
  if (playedAtHeader !== playedAtMeta) {
    fail(`meta playedAt (${playedAtMeta}) disagrees with header timestamp (${playedAtHeader})`);
  }
  li += 1;

  // --- TABLE ---------------------------------------------------------
  const tableMatch = matchOrFail(TABLE_RE, 'Expected Table line');
  const button = (Number(group(tableMatch, 4)) - 1) as Seat;
  li += 1;

  // --- SEATS ---------------------------------------------------------
  const seats: SeatEntry[] = [];
  while (li < lines.length) {
    const m = lines[li]?.match(SEAT_RE);
    if (!m) break;
    seats.push({
      seat: (Number(group(m, 1)) - 1) as Seat,
      playerId: group(m, 2) as PlayerId,
      startStack: Number(group(m, 3)),
    });
    li += 1;
  }
  if (seats.length === 0) fail('Expected at least one Seat line');

  // --- BLIND + ANTE POSTING -----------------------------------------
  const startStacks = new Map<PlayerId, number>(seats.map((s) => [s.playerId, s.startStack]));
  let ante = 0;
  const anteMatch = lines[li]?.match(ANTE_RE);
  if (anteMatch) {
    ante = Number(group(anteMatch, 2));
    li += 1;
  }
  const sbMatch = matchOrFail(SB_RE, 'Expected small blind post line');
  const sbId = group(sbMatch, 1) as PlayerId;
  li += 1;
  const bbMatch = matchOrFail(BB_RE, 'Expected big blind post line');
  const bbId = group(bbMatch, 1) as PlayerId;
  li += 1;
  const level: BlindLevel = { level: levelNum, sb, bb, ante };
  const { sbSeat: expectedSb, bbSeat: expectedBb } = blindSeats(button, seats);
  if (idBySeat(seats, expectedSb) !== sbId || idBySeat(seats, expectedBb) !== bbId) {
    fail(
      `Blind seat mismatch: expected SB=${idBySeat(seats, expectedSb)} BB=${idBySeat(seats, expectedBb)}, got SB=${sbId} BB=${bbId}`,
    );
  }

  // --- HOLE CARDS ----------------------------------------------------
  if (lines[li] !== '*** HOLE CARDS ***') fail('Expected *** HOLE CARDS *** marker');
  li += 1;
  const holeCardsByPlayer: Record<PlayerId, readonly [Card, Card]> = {};
  let heroId: PlayerId | undefined;
  while (li < lines.length) {
    const m = lines[li]?.match(DEALT_RE);
    if (!m) break;
    heroId = group(m, 1) as PlayerId;
    holeCardsByPlayer[heroId] = holeCardPair(group(m, 2));
    li += 1;
  }
  if (!heroId) fail('Expected Dealt to <hero> line after *** HOLE CARDS ***');

  // --- ACTIONS + STREET MARKERS -------------------------------------
  const actions: Action[] = [];
  const board: Card[] = [];
  const ctx = freshStreet('preflop');
  resetCtxForPreflop(ctx, sbId, bbId, level, startStacks);

  const parseActionLine = (line: string): Action | null => {
    const foldM = line.match(ACTION_FOLD);
    if (foldM) return { type: 'fold', playerId: group(foldM, 1) as PlayerId };
    const checkM = line.match(ACTION_CHECK);
    if (checkM) return { type: 'check', playerId: group(checkM, 1) as PlayerId };
    const callM = line.match(ACTION_CALL);
    if (callM) {
      const id = group(callM, 1) as PlayerId;
      const amount = Number(group(callM, 2));
      const isAllIn = !!callM[3];
      return isAllIn
        ? { type: 'allin', playerId: id, amount: (ctx.streetContrib.get(id) ?? 0) + amount }
        : { type: 'call', playerId: id, amount };
    }
    const betM = line.match(ACTION_BET);
    if (betM) {
      const id = group(betM, 1) as PlayerId;
      const amount = Number(group(betM, 2));
      const isAllIn = !!betM[3];
      return isAllIn
        ? { type: 'allin', playerId: id, amount: (ctx.streetContrib.get(id) ?? 0) + amount }
        : { type: 'bet', playerId: id, amount };
    }
    const raiseM = line.match(ACTION_RAISE);
    if (raiseM) {
      const id = group(raiseM, 1) as PlayerId;
      const to = Number(group(raiseM, 3));
      const isAllIn = !!raiseM[4];
      return isAllIn
        ? { type: 'allin', playerId: id, amount: to }
        : { type: 'raise', playerId: id, to };
    }
    return null;
  };

  while (li < lines.length) {
    const line = lines[li] as string;
    if (
      line === '*** SHOW DOWN ***' ||
      line === '*** SUMMARY ***' ||
      UNCALLED_RE.test(line) ||
      COLLECTED_RE.test(line)
    ) {
      break;
    }

    const streetM = line.match(STREET_RE);
    if (streetM) {
      const street = group(streetM, 1).toLowerCase() as Street;
      const cumulative = parseCardArray(group(streetM, 2));
      const newCard = streetM[3] ? parseCardArray(streetM[3]) : [];
      board.length = 0;
      board.push(...cumulative, ...newCard);
      ctx.street = street;
      ctx.currentBet = 0;
      ctx.streetContrib = new Map();
      li += 1;
      continue;
    }

    const action = parseActionLine(line);
    if (!action) fail('Unrecognized action/marker line');
    actions.push(action as Action);
    advanceCtx(ctx, action as Action, startStacks);
    li += 1;
  }

  // --- UNCALLED + COLLECTED + SHOWDOWN ------------------------------
  if (lines[li]?.match(UNCALLED_RE)) li += 1;

  const payouts: HandPayout[] = [];
  if (lines[li] === '*** SHOW DOWN ***') {
    li += 1;
    while (li < lines.length) {
      const m = lines[li]?.match(SHOWS_RE);
      if (!m) break;
      const id = group(m, 1) as PlayerId;
      const cards = holeCardPair(group(m, 2));
      const wonOrLost = group(m, 3);
      const wonAmount = m[4] ? Number(m[4]) : 0;
      const desc = m[5];
      holeCardsByPlayer[id] = cards;
      if (wonOrLost === 'won' && wonAmount > 0) {
        payouts.push({
          playerId: id,
          potIndex: 0,
          amount: wonAmount,
          handDescription: desc,
        });
      }
      li += 1;
    }
  } else {
    while (li < lines.length) {
      const m = lines[li]?.match(COLLECTED_RE);
      if (!m) break;
      const id = group(m, 1) as PlayerId;
      const amount = Number(group(m, 2));
      payouts.push({ playerId: id, potIndex: 0, amount });
      li += 1;
    }
  }

  // --- SUMMARY -------------------------------------------------------
  if (lines[li] !== '*** SUMMARY ***') fail('Expected *** SUMMARY *** marker');
  li += 1;
  const totalPotMatch = matchOrFail(TOTAL_POT_RE, 'Expected Total pot line');
  const totalPot = Number(group(totalPotMatch, 1));
  li += 1;
  const boardMatch = lines[li]?.match(BOARD_RE);
  if (boardMatch) {
    const parsed = parseCardArray(group(boardMatch, 1));
    if (board.length === 0) board.push(...parsed);
    li += 1;
  }
  while (li < lines.length) {
    const line = lines[li];
    if (!line) break;
    const muckMatch = line.match(SUMMARY_MUCKED_RE);
    if (muckMatch) {
      const id = group(muckMatch, 1) as PlayerId;
      holeCardsByPlayer[id] = holeCardPair(group(muckMatch, 2));
      li += 1;
      continue;
    }
    // "Seat X: id (roles?) showed [c c] and won/lost …" — covers the case
    // where a player won uncontested; the mid-section has no shows line
    // but SUMMARY still encodes the cards.
    const showedMatch = line.match(SUMMARY_SHOWED_RE);
    if (showedMatch) {
      const id = group(showedMatch, 1) as PlayerId;
      if (!holeCardsByPlayer[id]) {
        holeCardsByPlayer[id] = holeCardPair(group(showedMatch, 2));
      }
      li += 1;
      continue;
    }
    const sumMatch = line.match(SUMMARY_SEAT_RE);
    if (!sumMatch) break;
    li += 1;
  }

  return {
    tournamentId,
    handNumber,
    level,
    seed,
    playedAt: playedAtMeta,
    button,
    seats,
    heroId: heroId as PlayerId,
    holeCardsByPlayer,
    actions,
    board,
    payouts,
    totalPot,
  };
}

/** Split a multi-hand HH text into per-hand records. */
export function parseFile(text: string): HandRecord[] {
  const out: HandRecord[] = [];
  // Hand boundaries are blank lines between meta headers. We split on the
  // canonical "# meta: " marker so the parser sees each hand whole.
  const chunks: string[] = [];
  let current: string[] = [];
  for (const line of text.replace(/\r\n/g, '\n').split('\n')) {
    if (line.startsWith('# meta: ') && current.length > 0) {
      chunks.push(current.join('\n'));
      current = [];
    }
    current.push(line);
  }
  if (current.some((l) => l.length > 0)) chunks.push(current.join('\n'));
  for (const chunk of chunks) {
    const text = chunk.trim();
    if (text.length === 0) continue;
    out.push(parseHand(text));
  }
  return out;
}
